import axios, { AxiosError } from 'axios'
import { useAuthStore } from '../stores/auth'
import type { TokenResponse } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config
    
    // If 401 and we have refresh token, try to refresh
    if (error.response?.status === 401 && originalRequest) {
      const refreshToken = useAuthStore.getState().refreshToken
      
      if (refreshToken && !originalRequest.url?.includes('/auth/refresh')) {
        try {
          const response = await axios.post<TokenResponse>(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          })
          
          const { access_token, refresh_token } = response.data
          useAuthStore.getState().setTokens(access_token, refresh_token)
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        } catch {
          // Refresh failed - logout
          useAuthStore.getState().logout()
        }
      }
    }
    
    return Promise.reject(error)
  }
)

export default api


// =============================================================================
// People API helpers
// =============================================================================

import type {
  Person,
  PeopleStats,
  PaginatedResponse,
  ColumnMappingResponse,
  FilterGroup,
} from '../types'

export const peopleApi = {
  list: (params?: { page?: number; page_size?: number; search?: string; order_by?: string }) =>
    api.get<PaginatedResponse<Person>>('/people', { params }),

  get: (personId: string) =>
    api.get<Person>(`/people/${personId}`),

  filter: (body: { filters?: FilterGroup; page?: number; page_size?: number }) =>
    api.post<PaginatedResponse<Person>>('/people/filter', body),

  filterCount: (filters?: FilterGroup) =>
    api.post<{ count: number }>('/people/filter/count', { filters }),

  stats: () =>
    api.get<PeopleStats>('/people/stats'),

  columns: () =>
    api.get<{ columns: string[] }>('/people/columns'),

  rebuild: () =>
    api.post<{ status: string; total_people: number }>('/people/rebuild'),
}

export const mappingApi = {
  generate: (uploadId: string) =>
    api.post<ColumnMappingResponse>(`/uploads/${uploadId}/mapping`),

  confirm: (uploadId: string, mapping: Record<string, string | null>) =>
    api.post<{ status: string; dataset_id: string; people_synced: number }>(
      `/uploads/${uploadId}/confirm-mapping`,
      { mapping }
    ),

  get: (uploadId: string) =>
    api.get<ColumnMappingResponse>(`/uploads/${uploadId}/mapping`),
}
